import React, { useMemo, useEffect, useCallback, useState } from 'react';
import {
  Form,
  Popconfirm,
  notification,
  Row,
  Col,
  Checkbox,
  Input,
  DatePicker,
} from 'antd';
import { PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';

import DF from 'DisplayFramework';
import { indexBy, toDatePickerDate } from 'utils/utils2';
import Button from 'components/AddButtonSubEntity';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  ParAssetCatalogOptionResponse,
  getAssetCatalogOptionList,
  addAssetCatalogOption,
  updateAssetCatalogOption,
  removeAssetCatalogOption,
  AssetCatalogResponse,
  updateAssetCatalogOptionStatus,
} from 'services/assetconfig';
import TaxSelect from 'components/TaxSelect';
import { useFormList } from 'hooks/useFormList';
// import AssetUpload from 'components/AssetUpload';
import ParListSelect from 'components/ParListSelect';
// import InputMoney from 'components/InputMoney/InputMoney';
import DeleteButton from 'components/DeleteButton';
import { isNil } from 'lodash';
import { setValueByNamePath } from 'utils/utils2';
// import ImagePreviw from './components/ImagePreview';
import SaveButtonIcon from 'components/SaveBtn';
import ModalControllerNV from 'components/ModalControllerNV';
import CatalogOptionsLabelModal from './components/catalogOptionsLabelModal';
import CatalogOptionsConflictsModal from './components/catalogOptionsConflictsModal';
import ButtonOperation from 'components/ButtonOperation';
import UploadPictureAssetOptions from './components/uploadPictureAssetOptions';
import InputCurrency from 'components/InputCurrency';

const NEW_CATALOG_OPTION_ID = -1;

type AssetCatalogOptionsTableProps = {
  asset: AssetCatalogResponse;
  isModal?: string;
  objectreference: string;
};

const AssetCatalogOptionsTable = (props: AssetCatalogOptionsTableProps) => {
  const { catalogid } = props.asset;
  const { isModal, objectreference } = props;
  const [form] = Form.useForm();
  const fieldName = 'options';
  const [code, setCode] = useState('');
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'optid',
    addAsFirst: true,
  });

  const { data, refetch, isFetching } = useQuery(
    ['getAssetCatalogOptionList', catalogid],
    () =>
      getAssetCatalogOptionList({ catalogid })
        .then((res) => res.list.sort((a, b) => a.optid - b.optid) || [])
        .catch(() => {
          return [];
        }),
    { enabled: catalogid !== undefined }
  );
  const getName = useCallback(
    (name: string) => (record: ParAssetCatalogOptionResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'optid')[record.optid];
      return [fieldName, index, name];
    },
    [form]
  );
  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((record) => {
          return {
            ...record,
            startdate:
              record?.startdate != null
                ? toDatePickerDate(record?.startdate)
                : '',
            enddate:
              record?.enddate != null ? toDatePickerDate(record?.enddate) : '',
          };
        }),
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data, refetch]);

  const addRow = () => {
    const row: any = {
      optid: NEW_CATALOG_OPTION_ID,
      catalogid,
      opttaxcode: '',
      taxrate: 0,
      optvaluelabel: '',
      optvalue: '',
      optcode: '',
      optcodelabel: '',
      optnetamount: 0,
      opttaxamount: 0,
      optgrossamount: 0,
      optpictureurl: '',
      optpictureurl2: '',
      optpictureurl3: '',
      optpictureurl4: '',
      optpictureurl5: '',
      optpictureurl6: '',
      isEdited: false,
      flagfreelabel: false,
      flagtopropose: false,
    };
    add(row);
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(
        fieldName
      ) as ParAssetCatalogOptionResponse[];

      const newValues = values.map((item, i) => {
        if (i !== index) {
          return item;
        }

        const updatedItem: any = { ...item, ...updatedValues };
        const { optnetamount, taxrate } = updatedItem;
        const opttaxamount = optnetamount * (taxrate / 100);
        const optgrossamount = optnetamount + opttaxamount;

        return {
          ...updatedItem,
          optnetamount,
          opttaxamount,
          optgrossamount,
        };
      });

      form.setFieldsValue({
        [fieldName]: newValues,
      });
    },
    [form]
  );

  const onSave = (record: ParAssetCatalogOptionResponse) => {
    const steps: ParAssetCatalogOptionResponse[] =
      form.getFieldValue(fieldName);
    const index = indexBy(steps, 'optid')[record.optid];
    const currentValue = steps[index];

    const { optid } = currentValue;

    optid === NEW_CATALOG_OPTION_ID
      ? addAssetCatalogOption({
          catalogid,
          req: {
            ...currentValue,
            flagfreelabel: currentValue.flagfreelabel ?? false,
            flagtopropose: currentValue.flagtopropose ?? false,
          },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          }
        })
      : updateAssetCatalogOption({
          req: {
            ...currentValue,
            flagfreelabel: currentValue.flagfreelabel ?? false,
            flagtopropose: currentValue.flagtopropose ?? false,
          },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            form?.setFieldsValue({
              [fieldName]: form
                .getFieldValue(fieldName)
                .map((option: any) =>
                  option?.optid === currentValue?.optid
                    ? { ...option, isEdited: false }
                    : { ...option }
                ),
            });
            refetch();
          }
        });
  };

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'flagfreelabel',
        render: (_value, _record, index) => {
          return (
            <Form.Item name={getName('flagfreelabel')(_record)} noStyle>
              <Checkbox
                defaultChecked={form.getFieldValue(
                  getName('flagfreelabel')(_record)
                )}
                onChange={(e) => {
                  setValueByNamePath(
                    form,
                    getName('flagfreelabel')(_record),
                    e.target.checked
                  );
                }}
              />
            </Form.Item>
          );
        },
        width: 50,
      },
      {
        dataIndex: 'optcode',
        render: (_value, _record, index) => {
          return (
            <Form.Item name={getName('optcode')(_record)} noStyle>
              <ParListSelect
                parcode="OPTTYPE"
                onSelect={(e) => {
                  setCode(e);
                  form.setFieldsValue({ opttaxcode: '' });
                  setValueByNamePath(form, [fieldName, index, 'optvalue'], '');
                }}
              />
            </Form.Item>
          );
        },
        width: 180,
      },
      {
        dataIndex: 'optvalue',
        render: (_value, _record, index) => {
          return (
            <Form.Item noStyle shouldUpdate={() => true}>
              {({ getFieldValue }) => (
                <Form.Item noStyle name={[fieldName, index, 'optvalue']}>
                  <ParListSelect parcode="OPTVALUE" parvalueparent={code} />
                </Form.Item>
              )}
            </Form.Item>
          );
        },
        width: 200,
      },
      {
        dataIndex: 'optlabel',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[fieldName, index, 'optlabel']}
              noStyle
              shouldUpdate
            >
              <Input
                disabled={
                  !form.getFieldValue(getName('flagfreelabel')(_record))
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
          );
        },
        width: 250,
      },
      {
        dataIndex: 'flagtopropose',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
                'flagtopropose',
              ]}
              noStyle
            >
              <Checkbox
                defaultChecked={form.getFieldValue(
                  getName('flagtopropose')(_record)
                )}
                onChange={(e) => {
                  setValueByNamePath(
                    form,
                    getName('flagtopropose')(_record),
                    e.target.checked
                  );
                }}
              />
            </Form.Item>
          );
        },
        width: 50,
      },

      {
        dataIndex: 'optnetamount',
        render: (_value, _record, index) => {
          return (
            <Form.Item name={[fieldName, index, 'optnetamount']} noStyle>
              <InputCurrency
                style={{ width: '100%' }}
                onChange={(value) =>
                  recalculate(index, { optnetamount: value })
                }
                currcode={_record.currcode}
              />
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        comcode: 'opttaxcode',
        key: 'opttaxcode',
        render: (_value, _record, index) => {
          return (
            <Form.Item name={[fieldName, index, 'opttaxcode']} noStyle>
              <TaxSelect
                taxflow="INOUT"
                onSelected={(tax) =>
                  recalculate(index, { taxrate: tax.taxrate })
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
          );
        },

        width: 150,
      },
      {
        comcode: 'opttaxamount',
        key: 'opttaxamount',
        render: (_value, _record, index) => {
          return (
            <Form.Item noStyle name={[fieldName, index, 'opttaxamount']}>
              <InputCurrency
                style={{ width: '100%' }}
                disabled={_record.opttaxcode === 'NOTAX'}
                currcode={_record.currcode}
              />
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        comcode: 'optgrossamount',
        key: 'optgrossamount',
        render: (_value, _record, index) => {
          return (
            <Form.Item noStyle name={[fieldName, index, 'optgrossamount']}>
              <InputCurrency
                style={{ width: '100%' }}
                currcode={_record.currcode}
              />
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        comcode: 'optpictures',
        key: 'optpictures',
        render: (_value, _record, index) => {
          return (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <UploadPictureAssetOptions
                  onCancel={() => {
                    setVisible(false);
                  }}
                  visible={visible}
                  _record={_record}
                  onSave={() => {
                    setVisible(false);
                    onSave(_record);
                  }}
                  form={form}
                  fieldName={fieldName}
                  catalogid={catalogid}
                  refetch={refetch}
                  objectreference={objectreference}
                />
              )}
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}> list of pic</a>
              )}
            />
          );
        },
        width: 150,
      },
      // {
      //   dataIndex: 'optpictureurl',
      //   render: (_value, _record, index) => {
      //     const name = [
      //       fieldName,
      //       indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
      //       'optpictureurl',
      //     ];
      //     return _record?.optid === NEW_CATALOG_OPTION_ID ||
      //       _record?.isEdited ? (
      //       <AssetUpload
      //         catalogid={catalogid}
      //         opid={_record.optid}
      //         picid={1}
      //         namePath={name}
      //         form={form}
      //         refetch={refetch}
      //       />
      //     ) : _record?.optpictureurl === 'null' ? (
      //       ''
      //     ) : (
      //       <ImagePreviw
      //         fieldName={fieldName}
      //         form={form}
      //         _record={_record}
      //         name="optpictureurl"
      //       />
      //     );
      //   },
      //   width: 180,
      // },
      // {
      //   dataIndex: 'optpictureurl2',
      //   render: (_value, _record, index) => {
      //     const name = [
      //       fieldName,
      //       indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
      //       'optpictureurl2',
      //     ];
      //     return _record?.optid === NEW_CATALOG_OPTION_ID ||
      //       _record?.isEdited ? (
      //       <AssetUpload
      //         catalogid={catalogid}
      //         opid={_record.optid}
      //         picid={2}
      //         namePath={name}
      //         form={form}
      //         refetch={refetch}
      //       />
      //     ) : _record?.optpictureurl2 === 'null' ? (
      //       ''
      //     ) : (
      //       <ImagePreviw
      //         fieldName={fieldName}
      //         form={form}
      //         _record={_record}
      //         name="optpictureurl2"
      //       />
      //     );
      //   },
      //   width: 180,
      // },
      // {
      //   dataIndex: 'optpictureurl3',
      //   render: (_value, _record, index) => {
      //     const name = [
      //       fieldName,
      //       indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
      //       'optpictureurl3',
      //     ];
      //     return _record?.optid === NEW_CATALOG_OPTION_ID ||
      //       _record?.isEdited ? (
      //       <AssetUpload
      //         catalogid={catalogid}
      //         opid={_record.optid}
      //         picid={3}
      //         namePath={name}
      //         form={form}
      //         refetch={refetch}
      //       />
      //     ) : _record?.optpictureurl3 === 'null' ? (
      //       ''
      //     ) : (
      //       <ImagePreviw
      //         fieldName={fieldName}
      //         form={form}
      //         _record={_record}
      //         name="optpictureurl3"
      //       />
      //     );
      //   },
      //   width: 180,
      // },
      // {
      //   dataIndex: 'optpictureurl4',
      //   render: (_value, _record, index) => {
      //     const name = [
      //       fieldName,
      //       indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
      //       'optpictureurl4',
      //     ];
      //     return _record?.optid === NEW_CATALOG_OPTION_ID ||
      //       _record?.isEdited ? (
      //       <AssetUpload
      //         catalogid={catalogid}
      //         opid={_record.optid}
      //         picid={4}
      //         namePath={name}
      //         form={form}
      //         refetch={refetch}
      //       />
      //     ) : _record?.optpictureurl4 === 'null' ? (
      //       ''
      //     ) : (
      //       <ImagePreviw
      //         fieldName={fieldName}
      //         form={form}
      //         _record={_record}
      //         name="optpictureurl4"
      //       />
      //     );
      //   },
      //   width: 180,
      // },
      // {
      //   dataIndex: 'optpictureurl5',
      //   render: (_value, _record, index) => {
      //     const name = [
      //       fieldName,
      //       indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
      //       'optpictureurl5',
      //     ];
      //     return _record?.optid === NEW_CATALOG_OPTION_ID ||
      //       _record?.isEdited ? (
      //       <AssetUpload
      //         catalogid={catalogid}
      //         opid={_record.optid}
      //         picid={5}
      //         namePath={name}
      //         form={form}
      //         refetch={refetch}
      //       />
      //     ) : _record?.optpictureurl5 === 'null' ? (
      //       ''
      //     ) : (
      //       <ImagePreviw
      //         fieldName={fieldName}
      //         form={form}
      //         _record={_record}
      //         name="optpictureurl5"
      //       />
      //     );
      //   },
      //   width: 180,
      // },
      // {
      //   dataIndex: 'optpictureurl6',
      //   render: (_value, _record, index) => {
      //     const name = [
      //       fieldName,
      //       indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
      //       'optpictureurl6',
      //     ];
      //     return _record?.optid === NEW_CATALOG_OPTION_ID ||
      //       _record?.isEdited ? (
      //       <AssetUpload
      //         catalogid={catalogid}
      //         opid={_record.optid}
      //         picid={6}
      //         namePath={name}
      //         form={form}
      //         refetch={refetch}
      //       />
      //     ) : _record?.optpictureurl6 === 'null' ? (
      //       ''
      //     ) : (
      //       <ImagePreviw
      //         fieldName={fieldName}
      //         form={form}
      //         _record={_record}
      //         name="optpictureurl6"
      //       />
      //     );
      //   },
      //   width: 180,
      // },
      {
        dataIndex: 'startdate',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
                'startdate',
              ]}
              noStyle
            >
              <DatePicker />
            </Form.Item>
          );
        },
        width: 150,
        fixed: 'right',
      },

      {
        dataIndex: 'enddate',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'optid')[_record.optid],
                'enddate',
              ]}
              noStyle
            >
              <DatePicker />
            </Form.Item>
          );
        },
        width: 150,
        fixed: 'right',
      },

      {
        dataIndex: 'conflicts',
        render: (_value, _record, index) => {
          return (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <CatalogOptionsConflictsModal
                  onCancel={() => {
                    setVisible(false);
                  }}
                  visible={visible}
                  catalogOption={data ?? []}
                  optid={_record.optid}
                  catalogid={_record.catalogid}
                />
              )}
              content={({ setVisible, visible }) => (
                <a
                  onClick={() =>
                    _record?.optid !== NEW_CATALOG_OPTION_ID && setVisible(true)
                  }
                >
                  {' '}
                  list of conflicts
                </a>
              )}
            />
          );
        },
        width: 100,
        fixed: 'right',
      },
      {
        dataIndex: 'labels',
        render: (_value, _record, index) => {
          return (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <CatalogOptionsLabelModal
                  onCancel={() => {
                    setVisible(false);
                  }}
                  visible={visible}
                  optid={_record.optid}
                  catalogid={_record.catalogid}
                />
              )}
              content={({ setVisible, visible }) => (
                <a
                  onClick={() =>
                    _record?.optid !== NEW_CATALOG_OPTION_ID && setVisible(true)
                  }
                >
                  {' '}
                  list of labels
                </a>
              )}
            />
          );
        },
        width: 100,
        fixed: 'right',
      },

      {
        dataIndex: 'optstatuslabel',
        render: (_value, _record, index) => {
          return form.getFieldValue(getName('optstatuslabel')(_record));
        },
        width: 100,
        fixed: 'right',
      },

      {
        key: 'actions',
        render: (_, _record) => (
          <Row justify="center">
            <Col span={24}>
              <ButtonOperation
                comcode={
                  _record?.optstatus === 'ACTIVE'
                    ? 'disabled'
                    : _record?.optstatus === 'DISABLED'
                    ? 'terminate'
                    : 'activate'
                }
                icon={<SwapOutlined />}
                onClick={() => {
                  updateAssetCatalogOptionStatus({
                    catalogid: catalogid,
                    optid: _record.optid,
                    optstatus:
                      _record?.optstatus === 'ACTIVE'
                        ? 'DISABLED'
                        : _record?.optstatus === 'DISABLED'
                        ? 'END'
                        : 'ACTIVE',
                  }).then((res) => {
                    refetch();
                    notification.success({
                      message: res.message,
                    });
                  });
                }}
              />
            </Col>
            <Col span={7}>
              <SaveButtonIcon
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                onlyIcon
              />
            </Col>
            <Col span={7}>
              {_record.optid === NEW_CATALOG_OPTION_ID ? (
                <DeleteButton onlyIcon onClick={() => remove(_record.optid)} />
              ) : (
                <Popconfirm
                  title="Sure to delete?"
                  onConfirm={() => {
                    removeAssetCatalogOption({
                      optid: _record.optid,
                      catalogid: _record.catalogid,
                    }).then((res) => {
                      notification.success({ message: res.message });
                      refetch();
                    });
                  }}
                >
                  <DeleteButton onlyIcon />
                </Popconfirm>
              )}
            </Col>
          </Row>
        ),
        fixed: 'right',
        align: 'center',
        width: 150,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, onSave, recalculate, refetch, remove, code]
  );

  return (
    <DF.Provider pagcode="assetCatalogOptions">
      <Form form={form} initialValues={INITIAL_VALUES}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const options: ParAssetCatalogOptionResponse[] =
              getFieldValue(fieldName);

            const isNewItemInTable =
              options?.find(({ optid }) => optid === NEW_CATALOG_OPTION_ID) !==
              undefined;

            return (
              <DataGrid
                rowKey="optid"
                columns={columns}
                dataSource={options}
                hideFilters
                size="middle"
                titleComcode="assetCatalogOptions"
                extraActions={
                  <Button
                    type="primary"
                    disabled={isNewItemInTable}
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    comcode="add"
                    hidden={isNil(catalogid) || !!isModal}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES = {
  options: [],
};

export default AssetCatalogOptionsTable;
